import React, { useState } from "react";
import "./style.scss";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { baseUrl } from "../../../utils/apis";
import { returnErrors } from "../../../store/reducers/error";
import { setFlashMessage } from "../../../store/reducers/message";
import { deAuthenticate } from "../../../store/reducers/authSlice";
import { PURGE_Brand } from "../../../store/reducers/brandSlice";
import { clearPersistedData } from "../../../store/configureStore";
import Spinner from "../../../reusableComponents/Spinner";
import PasswordRequirements from "../../../reusableComponents/PasswordRequirements";
import { PASSWORD_RULES_MESSAGE, validatePassword } from "../../../utils/helpers/string";

// Shown instead of the app while the user still has the temporary password we
// issued (user.must_change_password). The backend only accepts this one call
// until the password is changed, so there is nothing else to show.
function ForcePasswordChange() {
  const dispatch = useDispatch();
  const idToken = useSelector((state) => state.auth.idToken);
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [isSpinner, setIsSpinner] = useState(false);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const signOut = async () => {
    dispatch(deAuthenticate());
    dispatch(PURGE_Brand());
    await clearPersistedData();
    window.location.href = "/signin";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.current_password || !form.new_password || !form.confirm) {
      dispatch(returnErrors("Please fill in all fields", 400));
      return;
    }
    if (!validatePassword(form.new_password)) {
      dispatch(returnErrors(PASSWORD_RULES_MESSAGE, 400));
      return;
    }
    if (form.new_password !== form.confirm) {
      dispatch(returnErrors("Passwords do not match", 400));
      return;
    }
    if (form.new_password === form.current_password) {
      dispatch(returnErrors("Your new password must be different from the temporary password", 400));
      return;
    }

    setIsSpinner(true);
    try {
      await axios.post(
        `${baseUrl}/auth/settings/change-password/`,
        { current_password: form.current_password, new_password: form.new_password },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      // The password change invalidates the old session, so sign in again with the new one
      setFlashMessage("Password updated. Please log in with your new password.");
      await signOut();
    } catch (error) {
      const detail = error.response?.data?.detail;
      dispatch(returnErrors(typeof detail === "string" ? detail : "Could not update your password. Please check the temporary password and try again.", 400));
      setIsSpinner(false);
    }
  };

  return (
    <div className="force_password_change">
      <form className="force_password_change__card" onSubmit={handleSubmit}>
        <h1>Set a new password</h1>
        <p>
          You're signed in with a temporary password. Please choose a new password to continue to your account.
        </p>

        <label htmlFor="current_password">Temporary password</label>
        <input id="current_password" name="current_password" type={show ? "text" : "password"} autoComplete="current-password" value={form.current_password} onChange={handleChange} />

        <label htmlFor="new_password">New password</label>
        <input id="new_password" name="new_password" type={show ? "text" : "password"} autoComplete="new-password" value={form.new_password} onChange={handleChange} />
        <PasswordRequirements password={form.new_password} />

        <label htmlFor="confirm">Confirm new password</label>
        <input id="confirm" name="confirm" type={show ? "text" : "password"} autoComplete="new-password" value={form.confirm} onChange={handleChange} />

        <label className="force_password_change__show">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show passwords
        </label>

        <button type="submit" disabled={isSpinner}>
          {isSpinner ? <Spinner /> : "Update password"}
        </button>
        <button type="button" className="force_password_change__logout" onClick={signOut}>
          Log out
        </button>
      </form>
    </div>
  );
}

export default ForcePasswordChange;
